import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, KeyRound, Shield } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import BrandMark from '../../components/BrandMark';
import PartnerMark from '../../components/PartnerMark';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'willkart_trusted_remembered_email';

const TrustedLogin = ({ subdomainFromHost }) => {
  const { subdomain: subdomainFromParams } = useParams();
  const subdomain = subdomainFromHost || subdomainFromParams;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [partnerInfo, setPartnerInfo] = useState(null);
  
  // Resolve the login source path (for logout redirect) — always use the subdomain/client login, never reset-password
  const getLoginSourcePath = () => {
    const path = window.location.pathname;
    if (path.includes('reset-password') || path.includes('trusted_setup')) {
      // Use subdomain-based path if available
      const resolvedSubdomain = subdomain || partnerInfo?.subdomain;
      if (resolvedSubdomain) return `https://${resolvedSubdomain}.willkart.ai`;
      return '/trusted/login';
    }
    return path;
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [step, setStep] = useState('email'); // 'email' | 'password' | 'setup' | 'forgot_password'
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const passwordInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });

  // Check for remembered email on mount (skip if on reset password page)
  useEffect(() => {
    const isResetPath = window.location.pathname.includes('reset-password');
    if (isResetPath) return;
    
    const savedEmail = localStorage.getItem(STORAGE_KEY);
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setIsReturningUser(true);
      setStep('password');
      setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 100);
    }
  }, []);

  // Handle switching accounts
  const handleSwitchAccount = () => {
    localStorage.removeItem(STORAGE_KEY);
    setFormData({ email: '', password: '', confirmPassword: '' });
    setIsReturningUser(false);
    setStep('email');
  };

  // Check for setup token or reset token in URL
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    const isTrustedSetup = searchParams.get('trusted_setup') === 'true';
    const isResetPath = window.location.pathname.includes('reset-password');
    
    if (tokenParam) {
      if (isResetPath) {
        setStep('reset_password_confirm');
        setFormData(prev => ({ ...prev, resetToken: tokenParam }));
      } else if (isTrustedSetup || window.location.pathname.includes('/trusted/setup')) {
        setStep('setup');
        setFormData(prev => ({ ...prev, setupToken: tokenParam }));
      } else {
        setStep('setup');
        setFormData(prev => ({ ...prev, setupToken: tokenParam }));
      }
    }
  }, [searchParams]);

  // Check if already logged in (skip if on reset password page)
  useEffect(() => {
    const token = localStorage.getItem('trustedToken');
    const isResetPath = window.location.pathname.includes('reset-password');
    if (token && (step === 'email' || step === 'password') && !isResetPath) {
      navigate(`/trusted/dashboard`);
    }
  }, [navigate, step]);

  // Fetch partner info
  useEffect(() => {
    const fetchPartnerInfo = async () => {
      try {
        const response = await axios.get(`${API}/subdomain/${subdomain}`);
        setPartnerInfo(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.detail || 'Partner not found');
        setLoading(false);
      }
    };

    if (subdomain) {
      fetchPartnerInfo();
    } else {
      setLoading(false);
    }
  }, [subdomain]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle email check — determine if user has password or needs setup
  const handleCheckEmail = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/trusted/check-email`, { email: formData.email });
      const { exists, has_password, status } = response.data;
      
      if (!exists) {
        toast.error('No account found with this email. Please check with the person who shared access with you.');
        return;
      }
      if (status === 'disabled') {
        toast.error('Your access has been revoked. Please contact the account holder.');
        return;
      }
      if (!has_password || status === 'pending') {
        // First-time user — needs to set up password
        toast.info('Please set up your password to activate your account.');
        setStep('setup_from_email');
        return;
      }
      // Existing user with password — show password input
      setStep('password');
      setTimeout(() => passwordInputRef.current?.focus(), 100);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/trusted/login`, {
        email: formData.email,
        password: formData.password,
        subdomain: subdomain || ''
      });

      // Save email for next visit
      localStorage.setItem(STORAGE_KEY, formData.email);
      localStorage.setItem('trustedToken', response.data.token);

      if (response.data.refresh_token) {

        localStorage.setItem('trustedAccessToken', response.data.access_token || response.data.token);

        localStorage.setItem('trustedRefreshToken', response.data.refresh_token);

      }
      localStorage.setItem('trustedName', response.data.trusted_contact.name);
      localStorage.setItem('trustedEmail', response.data.trusted_contact.email);
      localStorage.setItem('trustedLoginSource', getLoginSourcePath());
      
      toast.success('Login successful!');
      navigate('/trusted/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle password setup (from invitation link)
  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/trusted/set-password`, {
        token: formData.setupToken || searchParams.get('token'),
        password: formData.password
      });

      // Save email for next visit
      localStorage.setItem(STORAGE_KEY, response.data.trusted_contact.email);
      localStorage.setItem('trustedToken', response.data.token);

      if (response.data.refresh_token) {

        localStorage.setItem('trustedAccessToken', response.data.access_token || response.data.token);

        localStorage.setItem('trustedRefreshToken', response.data.refresh_token);

      }
      localStorage.setItem('trustedName', response.data.trusted_contact.name);
      localStorage.setItem('trustedEmail', response.data.trusted_contact.email);
      localStorage.setItem('trustedLoginSource', getLoginSourcePath());
      
      toast.success('Account activated successfully!');
      navigate('/trusted/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to set password';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle password setup from email check (no token)
  const handleSetPasswordFromEmail = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/trusted/set-password-by-email`, {
        email: formData.email,
        password: formData.password
      });

      localStorage.setItem(STORAGE_KEY, response.data.trusted_contact.email);
      localStorage.setItem('trustedToken', response.data.token);

      if (response.data.refresh_token) {

        localStorage.setItem('trustedAccessToken', response.data.access_token || response.data.token);

        localStorage.setItem('trustedRefreshToken', response.data.refresh_token);

      }
      localStorage.setItem('trustedName', response.data.trusted_contact.name);
      localStorage.setItem('trustedEmail', response.data.trusted_contact.email);
      localStorage.setItem('trustedLoginSource', getLoginSourcePath());
      
      toast.success('Account activated successfully!');
      navigate('/trusted/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to set password';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle reset password confirmation (from email link)
  const handleResetPasswordConfirm = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/trusted/reset-password`, {
        token: formData.resetToken || searchParams.get('token'),
        new_password: formData.password
      });
      localStorage.setItem('trustedToken', response.data.token);

      if (response.data.refresh_token) {

        localStorage.setItem('trustedAccessToken', response.data.access_token || response.data.token);

        localStorage.setItem('trustedRefreshToken', response.data.refresh_token);

      }
      localStorage.setItem('trustedName', response.data.trusted_contact.name);
      localStorage.setItem('trustedEmail', response.data.trusted_contact.email);
      localStorage.setItem(STORAGE_KEY, response.data.trusted_contact.email);
      localStorage.setItem('trustedLoginSource', getLoginSourcePath());
      toast.success('Password reset successfully!');
      navigate('/trusted/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'Reset link has expired') {
        toast.error('This reset link has expired. Please request a new one.');
        setStep('forgot_password');
      } else {
        toast.error(detail || 'Failed to reset password');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle forgot password request
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/trusted/forgot-password`, {
        email: formData.email,
        subdomain: subdomain || ''
      });
      
      toast.success('If an account exists, you will receive a reset link.');
      setStep('login');
    } catch (error) {
      toast.success('If an account exists, you will receive a reset link.');
      setStep('login');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          {partnerInfo?.logo_url ? (
            <img
              src={partnerInfo.logo_url}
              alt={partnerInfo.partner_name}
              className="h-16 mx-auto mb-4 object-contain"
            />
          ) : (
            <div className="flex items-center justify-center mb-4">
              <PartnerMark partnerInfo={partnerInfo} size="xl" showName={false} />
            </div>
          )}
          <h1 className="text-2xl font-bold text-white">
            {step === 'setup' || step === 'setup_from_email' ? 'Set Up Your Account' : step === 'reset_password_confirm' ? 'Reset Password' : step === 'password' ? (isReturningUser ? 'Welcome back' : 'Enter Password') : step === 'forgot_password' ? 'Reset Password' : 'Trusted Contact Portal'}
          </h1>
          <p className="text-slate-400 mt-2">
            {step === 'setup' || step === 'setup_from_email'
              ? 'Create a password to access shared documents'
              : step === 'reset_password_confirm'
              ? 'Enter your new password below'
              : step === 'forgot_password'
              ? 'Enter your email to receive a reset link'
              : step === 'password'
              ? 'Enter your password to continue'
              : 'Enter your email to get started'
            }
          </p>
          {partnerInfo && (
            <p className="text-slate-500 text-sm mt-2">
              Powered by {partnerInfo.partner_name}
            </p>
          )}
        </div>

        {/* Form Card */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-8 backdrop-blur-sm">
          {/* Step 1: Email Input */}
          {step === 'email' && (
            <form onSubmit={handleCheckEmail} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-slate-300 mb-2 block">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                    data-testid="trusted-email-input"
                    autoFocus
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold py-3"
                data-testid="trusted-continue-btn"
              >
                {isSubmitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900" />
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Step 2: Password Input (existing users) */}
          {step === 'password' && (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label className="text-slate-300 mb-2 block">Email</Label>
                <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <span className="text-amber-400 font-semibold">
                      {formData.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-400 text-sm truncate">{formData.email}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleSwitchAccount}
                    className="text-amber-400 hover:text-amber-300 text-sm whitespace-nowrap"
                    data-testid="trusted-switch-account-btn"
                  >
                    Change
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-300 mb-2 block">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    ref={passwordInputRef}
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                    data-testid="trusted-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold py-3"
                data-testid="trusted-login-btn"
              >
                {isSubmitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900" />
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              <button
                type="button"
                onClick={() => setStep('forgot_password')}
                className="w-full text-center text-amber-400 hover:text-amber-300 text-sm mt-4"
              >
                Forgot your password?
              </button>
            </form>
          )}

          {/* Setup Password Form (from invitation link or email check) */}
          {(step === 'setup' || step === 'setup_from_email') && (
            <form onSubmit={step === 'setup' ? handleSetPassword : handleSetPasswordFromEmail} className="space-y-5">
              {step === 'setup_from_email' && (
                <div>
                  <Label className="text-slate-300 mb-2 block">Email</Label>
                  <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                    <Mail className="w-5 h-5 text-slate-500" />
                    <p className="text-slate-400 text-sm truncate flex-1">{formData.email}</p>
                    <button type="button" onClick={handleSwitchAccount} className="text-amber-400 hover:text-amber-300 text-sm">Change</button>
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="password" className="text-slate-300 mb-2 block">
                  Create Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Minimum 8 characters"
                    className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                    data-testid="trusted-setup-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-slate-300 mb-2 block">
                  Confirm Password
                </Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm your password"
                    className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                    data-testid="trusted-setup-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold py-3"
                data-testid="trusted-setup-btn"
              >
                {isSubmitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900" />
                ) : (
                  <>
                    Activate Account
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Forgot Password Form */}
          {step === 'forgot_password' && (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-slate-300 mb-2 block">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                    data-testid="trusted-forgot-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold py-3"
                data-testid="trusted-forgot-btn"
              >
                {isSubmitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900" />
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <button
                type="button"
                onClick={() => setStep('email')}
                className="w-full text-center text-slate-400 hover:text-slate-300 text-sm mt-4"
              >
                Back to login
              </button>
            </form>
          )}

          {/* Reset Password Confirm Form (from email link) */}
          {step === 'reset_password_confirm' && (
            <form onSubmit={handleResetPasswordConfirm} className="space-y-5">
              <div>
                <Label htmlFor="password" className="text-slate-300 mb-2 block">
                  New Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter new password (min 8 chars)"
                    className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                    data-testid="trusted-reset-password-input"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-slate-300 mb-2 block">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm new password"
                    className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                    data-testid="trusted-reset-confirm-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold py-3"
                data-testid="trusted-reset-submit-btn"
              >
                {isSubmitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900" />
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          Powered by <span className="text-amber-400">WillKart.ai</span>
        </p>
      </div>
    </div>
  );
};

export default TrustedLogin;
